import { cn } from '@repo/shared-client/lib/utils'
import { Link } from '@tanstack/react-router'
import { isNonProductionEnvironment } from '@web/src/lib/app-environment'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import type { CSSProperties } from 'react'
import { useSFContext } from 'src/contexts/sf-context'
import { useTranslation } from 'src/lib/intl/react'
import { useThemeLogos } from 'src/theme/logos'

/** Stacked logo asset viewBox is 360×132 (see `assets/original_images/logo/logo-stacked.svg`). */
const LOGO_STACKED_ASPECT = 132 / 360

const LOGO_WIDTH_PX: Record<'normal' | 'sm' | 'md' | 'lg', number> = {
  normal: 90,
  sm: 130,
  md: 200,
  lg: 250,
}

const justifyToClass: Record<string, string> = {
  center: 'justify-center',
  'flex-start': 'justify-start',
  'flex-end': 'justify-end',
  'space-between': 'justify-between',
  'space-around': 'justify-around',
  'space-evenly': 'justify-evenly',
}

export type LogoFullProps = {
  size?: keyof typeof LOGO_WIDTH_PX
  justify?: CSSProperties['justifyContent']
}

const LogoFull = ({ size = 'normal', justify = 'center' }: LogoFullProps) => {
  const { betaPage } = useSFContext()
  const { t } = useTranslation()
  const { full: stackedPicture } = useThemeLogos()
  const widthPx = LOGO_WIDTH_PX[size]
  const heightPx = Math.max(1, Math.round(widthPx * LOGO_STACKED_ASPECT))
  const pictureSizes = `${widthPx}px`

  const justifyClass = (justify && justifyToClass[String(justify)]) ?? 'justify-center'

  return (
    <nav className={cn('flex items-center gap-4', justifyClass)}>
      <Link className="relative z-10 inline-flex cursor-pointer touch-manipulation" to="/">
        <div className="relative inline-block">
          <picture>
            {Object.entries(stackedPicture.sources).map(([format, srcSet]) => (
              <source key={format} sizes={pictureSizes} srcSet={srcSet} type={`image/${format}`} />
            ))}
            <img
              alt={t('Spicy Friends logo')}
              className="h-auto max-w-none select-none [-webkit-touch-callout:none]"
              decoding="async"
              draggable={false}
              height={heightPx}
              sizes={pictureSizes}
              src={stackedPicture.img.src}
              width={widthPx}
            />
          </picture>
          {isNonProductionEnvironment() && (
            <span className="absolute right-0 bottom-0 translate-x-[110%] animate-pulse text-center font-bold text-red-500 capitalize">
              {getAppConfig().DOPPLER_ENVIRONMENT}
              <br />
              {t('Site')}
            </span>
          )}
          {betaPage && (
            <span className="absolute right-0 bottom-0 translate-x-1/2 translate-y-1/2 font-bold text-blue-400 text-xs tracking-wider">
              {t('BETA')}
            </span>
          )}
        </div>
      </Link>
    </nav>
  )
}

export default LogoFull
